import type {
  CategoryValue,
  ColorValue,
  QuizAnswerKey,
  StyleValue,
} from './types';

// Value arrays (variables live in constants)
export const CATEGORY_VALUES = [
  'women',
  'men',
  'kids',
  'no-preference',
] as const;

export const STYLE_VALUES = [
  'minimalism-casual',
  'designer-luxe',
  'romantic-boho',
  'sporty-casual',
  'alt-vintage',
  'street-urban',
  'classic-essential',
  'mini-me',
  'character-bold',
] as const;

export const COLOR_VALUES = [
  'warm-earthy',
  'bold-reds-pinks',
  'neutrals-metallics',
  'brights-neons',
  'pastels-soft-hues',
] as const;

// Shared style options for adult categories
const ADULT_STYLES: StyleValue[] = [
  'minimalism-casual',
  'designer-luxe',
  'romantic-boho',
  'sporty-casual',
  'alt-vintage',
  'street-urban',
];

const KIDS_STYLES: StyleValue[] = [
  'classic-essential',
  'mini-me',
  'character-bold',
];

export const STYLE_OPTIONS_BY_CATEGORY: Record<CategoryValue, StyleValue[]> = {
  women: ADULT_STYLES,
  men: ADULT_STYLES,
  kids: KIDS_STYLES,
  'no-preference': ADULT_STYLES,
};

export const COLOR_PALETTE: Record<ColorValue, string[]> = {
  'warm-earthy': ['#53372C', '#82741D', '#EFE2A9', '#EEB374'],
  'bold-reds-pinks': ['#F359AA', '#DC0A04', '#7F2626', '#F95906'],
  'neutrals-metallics': ['#000000', '#D7D7D7', '#C4A73F', '#FFFFFF'],
  'brights-neons': ['#FADD20', '#5417CB', '#DC0A04', '#54A60D'],
  'pastels-soft-hues': ['#F95906', '#F359AA', '#EFE2A9', '#EEB374'],
};

/**
 * Maps quiz color preferences to product filter color IDs.
 * Used to pre-filter products on quiz results pages.
 */
export const QUIZ_COLOR_TO_FILTER_COLORS: Record<ColorValue, string[]> = {
  'warm-earthy': ['brown', 'khaki', 'cream', 'tan'],
  'bold-reds-pinks': ['pink', 'red', 'burgundy', 'orange'],
  'neutrals-metallics': ['black', 'white', 'gold', 'silver'],
  'brights-neons': ['yellow', 'red', 'purple', 'green'],
  'pastels-soft-hues': ['orange', 'pink', 'cream', 'tan'],
};

export const DEFAULT_QUIZ_ANSWERS = {
  category: null,
  style: null,
  colors: null,
  finishedQuiz: false,
} as const;

/**
 * Explore path slugs by category and style.
 * Women/men use -ww/-mw suffix; kids use -kw; no-preference has no suffix.
 * These slugs should match the file names in Cloudfront static assets.
 * For example, the slug 'the-minimalist-ww' should match the file name 'the-minimalist-ww.png'.
 */
export const QUIZ_RESULTS_SLUG: Record<
  CategoryValue,
  Partial<Record<StyleValue, string>>
> = {
  women: {
    'designer-luxe': 'the-intentional-curator-ww',
    'street-urban': 'the-street-visionary-ww',
    'romantic-boho': 'the-dreamy-romantic-ww',
    'minimalism-casual': 'the-minimalist-ww',
    'alt-vintage': 'the-vintage-archivist-ww',
    'sporty-casual': 'the-sporty-nomad-ww',
  },
  men: {
    'designer-luxe': 'the-intentional-curator-mw',
    'street-urban': 'the-street-visionary-mw',
    'romantic-boho': 'the-dreamy-romantic-mw',
    'minimalism-casual': 'the-minimalist-mw',
    'alt-vintage': 'the-vintage-archivist-mw',
    'sporty-casual': 'the-sporty-nomad-mw',
  },
  kids: {
    'classic-essential': 'the-growth-strategist-kw',
    'mini-me': 'the-trend-driven-parent-kw',
    'character-bold': 'the-playful-curator-kw',
  },
  'no-preference': {
    'designer-luxe': 'the-intentional-curator',
    'street-urban': 'the-street-visionary',
    'romantic-boho': 'the-dreamy-romantic',
    'minimalism-casual': 'the-minimalist',
    'alt-vintage': 'the-vintage-archivist',
    'sporty-casual': 'the-sporty-nomad',
  },
};

export enum QuizStep {
  Category = 1,
  Style = 2,
  Colors = 3,
}

export const STEP_TO_ANSWER_KEY: Record<QuizStep, QuizAnswerKey> = {
  [QuizStep.Category]: 'category',
  [QuizStep.Style]: 'style',
  [QuizStep.Colors]: 'colors',
};

export const TOTAL_STEPS = Object.keys(STEP_TO_ANSWER_KEY).length;
